import {
    useState
} from "react";
import {
    ComponentHeader,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    RadioButton,
    RadioGroup,
    Field
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "RadioGroup";

const USAGE_CODE = `const [frequency, setFrequency] = useState("daily");

<Field>
    <Field.Title>
        Notification frequency
    </Field.Title>
    <Field.Description>
        How often we email you a summary.
    </Field.Description>
    <RadioGroup
        onValueChange={setFrequency}
        value={frequency}
    >
        <RadioButton
            title="Instantly"
            value="instant"
        />
        <RadioButton
            title="Once a day"
            value="daily"
        />
        <RadioButton
            title="Once a week"
            value="weekly"
        />
    </RadioGroup>
</Field>`;

const ERROR_CODE = `<Field>
    <Field.Title>
        Priority
    </Field.Title>
    <RadioGroup
        onValueChange={setPriority}
        value={priority}
        isRequired={true}
        isError={priority === undefined}
    >
        <RadioButton
            title="Low"
            value="low"
        />
        <RadioButton
            title="High"
            value="high"
        />
    </RadioGroup>
    <Field.Error
        errors={priority === undefined ? ["Choose one option."] : []}
    />
</Field>`;

const HORIZONTAL_CODE = `<RadioGroup
    onValueChange={setLayout}
    orientation="horizontal"
    aria-label="View"
    value={layout}
>
    <RadioButton
        title="List"
        value="list"
    />
    <RadioButton
        title="Grid"
        value="grid"
    />
    <RadioButton
        title="Board"
        value="board"
    />
</RadioGroup>`;

const DISABLED_CODE = `<RadioGroup
    defaultValue="monthly"
    isDisabled={true}
    aria-label="Billing"
>
    <RadioButton
        title="Monthly"
        value="monthly"
    />
    <RadioButton
        title="Yearly"
        value="yearly"
    />
</RadioGroup>`;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("radioGroupPage.requiredTitle"),
        id: "required"
    },
    {
        title: t("radioGroupPage.orientationTitle"),
        id: "orientation"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const RadioGroupPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        frequency,
        setFrequency
    ] = useState("daily");

    const [
        priority,
        setPriority
    ] = useState<string | undefined>(undefined);

    const [
        layout,
        setLayout
    ] = useState("list");

    return <Article>
        <ComponentHeader
            lead={t("radioGroupPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("radioGroupPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("radioGroupPage.overviewCaption")}
            >
                <Field>
                    <Field.Title>
                        {t("radioGroupPage.frequencyTitle")}
                    </Field.Title>
                    <Field.Description>
                        {t("radioGroupPage.frequencyText")}
                    </Field.Description>
                    <RadioGroup
                        onValueChange={setFrequency}
                        value={frequency}
                    >
                        <RadioButton
                            title={t("radioGroupPage.instant")}
                            value="instant"
                        />
                        <RadioButton
                            title={t("radioGroupPage.daily")}
                            value="daily"
                        />
                        <RadioButton
                            title={t("radioGroupPage.weekly")}
                            value="weekly"
                        />
                    </RadioGroup>
                </Field>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("radioGroupPage.requiredTitle")}
            id="required"
        >
            <Paragraph>
                {t("radioGroupPage.requiredText")}
            </Paragraph>
            <Preview
                caption={t("radioGroupPage.requiredCaption")}
            >
                <Field>
                    <Field.Title>
                        {t("radioGroupPage.priorityTitle")}
                    </Field.Title>
                    <RadioGroup
                        isError={priority === undefined}
                        onValueChange={setPriority}
                        isRequired={true}
                        value={priority}
                    >
                        <RadioButton
                            title={t("radioGroupPage.low")}
                            value="low"
                        />
                        <RadioButton
                            title={t("radioGroupPage.high")}
                            value="high"
                        />
                    </RadioGroup>
                    <Field.Error
                        errors={priority === undefined ? [
                            t("radioGroupPage.priorityError")
                        ] : []}
                    />
                </Field>
            </Preview>
            <CodeBlock
                code={ERROR_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("radioGroupPage.orientationTitle")}
            id="orientation"
        >
            <Paragraph>
                {t("radioGroupPage.orientationText")}
            </Paragraph>
            <Preview>
                <RadioGroup
                    aria-label={t("radioGroupPage.layoutTitle")}
                    onValueChange={setLayout}
                    orientation="horizontal"
                    value={layout}
                >
                    <RadioButton
                        title={t("radioGroupPage.list")}
                        value="list"
                    />
                    <RadioButton
                        title={t("radioGroupPage.grid")}
                        value="grid"
                    />
                    <RadioButton
                        title={t("radioGroupPage.board")}
                        value="board"
                    />
                </RadioGroup>
            </Preview>
            <CodeBlock
                code={HORIZONTAL_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("radioGroupPage.statesText")}
            </Paragraph>
            <Preview>
                <RadioGroup
                    aria-label={t("radioGroupPage.billingTitle")}
                    defaultValue="monthly"
                    isDisabled={true}
                >
                    <RadioButton
                        title={t("radioGroupPage.monthly")}
                        value="monthly"
                    />
                    <RadioButton
                        title={t("radioGroupPage.yearly")}
                        value="yearly"
                    />
                </RadioGroup>
            </Preview>
            <CodeBlock
                code={DISABLED_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("radioGroupPage.a11yRole"),
                    t("radioGroupPage.a11yField"),
                    t("radioGroupPage.a11yKeyboard"),
                    t("radioGroupPage.a11yLabel")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <Paragraph>
                {t("radioGroupPage.propsGroupIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("radioGroupPage.propValue"),
                        name: "value",
                        type: "string"
                    },
                    {
                        description: t("radioGroupPage.propDefaultValue"),
                        name: "defaultValue",
                        type: "string"
                    },
                    {
                        description: t("radioGroupPage.propOnValueChange"),
                        type: "(value: string) => void",
                        name: "onValueChange"
                    },
                    {
                        description: t("radioGroupPage.propOrientation"),
                        type: "\"vertical\" | \"horizontal\"",
                        defaultValue: "\"vertical\"",
                        name: "orientation"
                    },
                    {
                        description: t("radioGroupPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("radioGroupPage.propIsRequired"),
                        defaultValue: "false",
                        name: "isRequired",
                        type: "boolean"
                    },
                    {
                        description: t("radioGroupPage.propIsError"),
                        defaultValue: "false",
                        name: "isError",
                        type: "boolean"
                    },
                    {
                        description: t("radioGroupPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("radioGroupPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("radioGroupPage.propsNote")}
            </Paragraph>
            <Paragraph>
                {t("radioGroupPage.propsButtonIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("radioGroupPage.buttonPropValue"),
                        name: "value",
                        type: "string"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default RadioGroupPage;
